import React, { useCallback, useState } from 'react';

import { Modal, Button, Typography, message, Input, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '@services/api';
import { EditOutlined } from '@ant-design/icons';

const { Paragraph } = Typography;

interface ICustomerChangeNickNameModal {
  idToAdd: number;
  nickName?: string;
  mutate: () => Promise<any>;
}

const CustomerChangeNickNameModal: React.FC<ICustomerChangeNickNameModal> = ({
  idToAdd,
  nickName = '',
  mutate,
}) => {
  const { t } = useTranslation('customer-contracts');
  const [modalIsVisible, setModalIsVisible] = useState(false);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [nickNameValue, setNickNameValue] = useState(nickName);

  const putChangeNickname = useCallback(
    (id: any, payload: any) =>
      api.put(
        `/service-proxy/contracts/customer/updateNickname/${id}`,
        payload,
      ),
    [],
  );

  const handleClose = useCallback(() => {
    setModalIsVisible(false);
    setNickNameValue('');
    setConfirmLoading(false);
  }, []);

  const handleFinish = useCallback(async () => {
    setConfirmLoading(true);
    try {
      const data = {
        nickname: nickNameValue,
      };
      await putChangeNickname(idToAdd, data);
      message.success(t('modal-update-nickname.success-to-change'));
      mutate();
      handleClose();
    } catch (error) {
      // @ts-ignore
      const status = error?.response?.status;
      if (status === 404) {
        message.error(t('modal-update-nickname.request-not-found'));
      } else {
        message.error(t('modal-update-nickname.error-to-change'));
      }
    }
    setConfirmLoading(false);
  }, [idToAdd, t, handleClose, nickNameValue, putChangeNickname, mutate]);

  return (
    <>
      <Tooltip title={t('modal-update-nickname.editButton')}>
        <Button
          style={{ padding: '0 8px', marginLeft: 8 }}
          className="button-edit"
          onClick={() => setModalIsVisible(true)}
        >
          <EditOutlined />
        </Button>
      </Tooltip>
      <Modal
        bodyStyle={{ marginBottom: '10px' }}
        footer={
          <Button
            loading={confirmLoading}
            type="primary"
            onClick={handleFinish}
          >
            {t('modal-update-nickname.submit')}
          </Button>
        }
        maskClosable={false}
        okButtonProps={{ form: 'customerForm', htmlType: 'submit' }}
        style={{ maxWidth: 480 }}
        title={t('modal-update-nickname.title')}
        open={modalIsVisible}
        width="100%"
        centered
        onCancel={handleClose}
        afterClose={() => document.body.style.removeProperty('overflow')}
      >
        <Paragraph>{t('modal-update-nickname.description')}</Paragraph>
        <div style={{ textAlign: 'center' }}>
          <Input
            defaultValue={nickNameValue}
            size="large"
            onChange={event => setNickNameValue(event.target.value)}
          />
        </div>
      </Modal>
    </>
  );
};

export default CustomerChangeNickNameModal;
